import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { APP_NAME } from "@/lib/brand";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Upload, Trash2, Building2, Key, Eye, EyeOff } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import BillingInfo from "./BillingInfo";

const CorporateInfo = () => {
  const { clientInfo, profile } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [companyName, setCompanyName] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  // Claude API key state
  const [claudeApiKey, setClaudeApiKey] = useState("");
  const [hasExistingKey, setHasExistingKey] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [savingKey, setSavingKey] = useState(false);

  useEffect(() => {
    if (!clientInfo) return;
    setCompanyName(clientInfo.name || "");
    // Fetch logo_url and claude_api_key from client table
    const fetchClientData = async () => {
      const { data } = await supabase
        .from("client")
        .select("logo_url, claude_api_key")
        .eq("id", clientInfo.id)
        .single();
      if (data?.logo_url) setLogoUrl(data.logo_url);
      if (data?.claude_api_key) {
        setHasExistingKey(true);
        setClaudeApiKey(data.claude_api_key);
      }
      setLoading(false);
    };
    fetchClientData();
  }, [clientInfo]);

  const handleSaveName = async () => {
    if (!clientInfo) return;
    setSaving(true);
    const { error } = await supabase
      .from("client")
      .update({ name: companyName.trim() })
      .eq("id", clientInfo.id);
    setSaving(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Saved", description: "Company name updated successfully." });
    }
  };

  const handleSaveClaudeKey = async () => {
    if (!clientInfo) return;
    setSavingKey(true);
    const keyToSave = claudeApiKey.trim() || null;
    const { error } = await supabase
      .from("client")
      .update({ claude_api_key: keyToSave })
      .eq("id", clientInfo.id);
    setSavingKey(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setHasExistingKey(!!keyToSave);
      toast({
        title: "Saved",
        description: keyToSave
          ? "Claude API key saved. Your own key will be used for AI queries."
          : "Claude API key removed. The platform key will be used.",
      });
    }
  };

  const handleRemoveClaudeKey = async () => {
    if (!clientInfo) return;
    setSavingKey(true);
    const { error } = await supabase
      .from("client")
      .update({ claude_api_key: null })
      .eq("id", clientInfo.id);
    setSavingKey(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setClaudeApiKey("");
      setHasExistingKey(false);
      setShowApiKey(false);
      toast({ title: "Removed", description: "Claude API key removed. The platform key will be used." });
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !clientInfo) return;

    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid file", description: "Please upload an image file.", variant: "destructive" });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: "File too large", description: "Logo must be under 2 MB.", variant: "destructive" });
      return;
    }

    setUploading(true);
    const ext = file.name.split(".").pop();
    const filePath = `${clientInfo.id}/logo.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from("client-logos")
      .upload(filePath, file, { upsert: true });

    if (uploadError) {
      setUploading(false);
      toast({ title: "Upload failed", description: uploadError.message, variant: "destructive" });
      return;
    }

    const { data: urlData } = supabase.storage.from("client-logos").getPublicUrl(filePath);
    const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;

    await supabase.from("client").update({ logo_url: publicUrl }).eq("id", clientInfo.id);

    setLogoUrl(publicUrl);
    setUploading(false);
    toast({ title: "Uploaded", description: "Logo uploaded successfully." });

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveLogo = async () => {
    if (!clientInfo || !logoUrl) return;
    setSaving(true);
    await supabase.from("client").update({ logo_url: null }).eq("id", clientInfo.id);
    setLogoUrl(null);
    setSaving(false);
    toast({ title: "Removed", description: "Logo removed." });
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 md:p-6 overflow-auto">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Building2 className="h-6 w-6" /> Corporate Information
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your company name and logo. These appear on exported reports and PDFs.
        </p>
      </div>

      {/* Company Name */}
      <div className="max-w-md space-y-2">
        <Label htmlFor="company-name">Company Name</Label>
        <div className="flex gap-2">
          <Input
            id="company-name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Enter company name"
          />
          <Button onClick={handleSaveName} disabled={saving || !companyName.trim()}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </div>

      {/* Logo Upload */}
      <div className="max-w-md space-y-3">
        <Label>Company Logo</Label>
        {logoUrl ? (
          <div className="space-y-3">
            <div className="flex items-center gap-4 rounded-lg border border-border bg-muted/30 p-4">
              <img
                src={logoUrl}
                alt="Company logo"
                className="h-16 w-16 rounded-md object-contain bg-background border border-border"
              />
              <div className="flex-1">
                <p className="text-sm font-medium">Current logo</p>
                <p className="text-xs text-muted-foreground">Click below to replace or remove</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                Replace
              </Button>
              <Button variant="outline" size="sm" onClick={handleRemoveLogo} disabled={saving}>
                <Trash2 className="h-4 w-4 mr-1" /> Remove
              </Button>
            </div>
          </div>
        ) : (
          <div
            className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border p-8 cursor-pointer hover:bg-muted/30 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? (
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            ) : (
              <>
                <Upload className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Click to upload logo (max 2 MB)</p>
              </>
            )}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleLogoUpload}
        />
      </div>

      {/* Claude API Key */}
      <div className="border-t border-border pt-6">
        <div className="max-w-md space-y-3">
          <div className="flex items-center gap-2">
            <Key className="h-5 w-5 text-muted-foreground" />
            <Label className="text-base font-semibold">Claude API Key</Label>
            {hasExistingKey ? (
              <Badge variant="secondary" className="text-green-700 bg-green-50">Client Key Active</Badge>
            ) : (
              <Badge variant="secondary" className="text-muted-foreground">Using Platform Key</Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Provide your own Anthropic Claude API key to use for AI queries. If not set, the shared {APP_NAME} platform key will be used.
          </p>
          <div className="space-y-2">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="claude-api-key"
                  type={showApiKey ? "text" : "password"}
                  value={claudeApiKey}
                  onChange={(e) => setClaudeApiKey(e.target.value)}
                  placeholder="sk-ant-api03-..."
                  className="pr-10 font-mono text-sm"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button onClick={handleSaveClaudeKey} disabled={savingKey}>
                {savingKey ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </div>
            {hasExistingKey && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemoveClaudeKey}
                disabled={savingKey}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-1" /> Remove Key
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Billing Information */}
      <div className="border-t border-border pt-6">
        <BillingInfo />
      </div>
    </div>
  );
};

export default CorporateInfo;
